import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog"
import { Button } from "@/components/ui/button"
import {
  DynamicList,
  KpiFieldRow,
} from "@/pages/App/Plan/Intake/components/DynamicList"

// Inline KPI/KOI definition editor. Mounted on the dashboard from a
// button next to the KPI scorecard heading. Submits the entire
// definition array (sparse-PATCH at the row level isn't worth it -- KPI
// lists are tiny and editing one line typically implies editing
// neighbors). The server reconciles the current month's kpi_results
// in the same transaction so the +/=/- toggles update immediately.
//
// Props:
//   kind        "kpi" | "koi"
//   open        controlled
//   onOpenChange controlled
//   plan        { id, annualKpis, annualKois, lockVersion }
//   onSaved     optional callback after successful save
export function KpiEditDialog({ kind, open, onOpenChange, plan, onSaved }) {
  const initialItems = (kind === "koi" ? plan?.annualKois : plan?.annualKpis) || []
  const [rows, setRows] = useState(() => seedRows(initialItems))
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  // Re-seed when the dialog re-opens or the plan prop's source array
  // changes (e.g., a successful save reloaded the page props).
  useEffect(() => {
    if (open) {
      setRows(seedRows(initialItems))
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?.lockVersion])

  const heading = kind === "koi" ? "Edit Annual KOIs" : "Edit Annual KPIs"
  const namePlaceholder =
    kind === "koi"
      ? "KOI name (e.g., Weekly Sabbath rest)"
      : "KPI name (e.g., Monthly Recurring Revenue)"
  const targetPlaceholder = kind === "koi" ? "Target (e.g., 4/mo)" : "Target (e.g., $50k)"
  const paramKey = kind === "koi" ? "annual_kois" : "annual_kpis"

  const submit = (e) => {
    e?.preventDefault?.()
    if (processing) return
    setProcessing(true)
    setError(null)

    // Drop empty rows server-side validates them, but pruning here
    // keeps the UI honest about what will land.
    const cleaned = rows
      .map((r) => ({ ...r, name: (r.name || "").trim(), target: (r.target || "").trim() }))
      .filter((r) => r.name)

    fetch(`/app/planning/annual_plans/${plan.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        Accept: "application/json",
      },
      body: JSON.stringify({ [paramKey]: cleaned, lock_version: plan.lockVersion }),
      credentials: "same-origin",
    })
      .then(async (res) => {
        if (res.ok) {
          onOpenChange(false)
          // Reload the dashboard's plan + scorecard so the new defs render.
          router.reload({ only: ["annualPlan", "scorecard", "currentMonth"] })
          onSaved?.()
        } else {
          const json = await res.json().catch(() => ({}))
          setError(json.error || "Could not save. Please try again.")
        }
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setProcessing(false))
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-2xl">
        <form onSubmit={submit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {heading}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              3–5 metrics you watch all year. Edits save instantly and the current month's
              scorecard adds new entries automatically.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="px-4 sm:px-6 py-4">
            {error && (
              <div
                role="alert"
                className="mb-3 flex items-center justify-between gap-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
              >
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-red-700 hover:text-red-900"
                >
                  Dismiss
                </button>
              </div>
            )}

            <DynamicList
              items={rows}
              onChange={setRows}
              min={1}
              max={10}
              newItem={() => ({ name: "", target: "" })}
              addLabel={kind === "koi" ? "Add another KOI" : "Add another KPI"}
              render={(item, update) => (
                <KpiFieldRow
                  item={item}
                  update={update}
                  namePlaceholder={namePlaceholder}
                  targetPlaceholder={targetPlaceholder}
                />
              )}
            />
          </div>

          <ResponsiveDialogFooter>
            <Button
              type="button"
              variant="kf-outline"
              onClick={() => onOpenChange(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button type="submit" variant="gradient" disabled={processing}>
              {processing ? "Saving…" : "Save"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// Seed editable rows from server data; ensures an id-stable key for
// each row even before the server has stamped one.
function seedRows(items) {
  if (!items || items.length === 0) {
    return [{ name: "", target: "" }]
  }
  return items.map((it) => ({
    id: it.id,
    name: it.name || "",
    target: it.target || "",
    unit: it.unit || "",
  }))
}
